// src/app/modules/Events/pages/EventDetail.jsx
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit2, Send, XCircle, Ticket, DollarSign, Users, Calendar, MapPin, Trash2 } from 'lucide-react';
import * as actions from '../../Dashbord/_redux/dashboard/Actions';
import './events.scss';

const statusColor = { draft: '#94A3B8', published: '#16A34A', cancelled: '#DC2626', ended: '#6B7280' };
const statusBg    = { draft: '#F1F5F9', published: '#DCFCE7', cancelled: '#FEE2E2', ended: '#F3F4F6' };

export function EventDetail() {
  const { eventId } = useParams();
  const dispatch    = useDispatch();
  const navigate    = useNavigate();

  const { org }    = useSelector(s => ({ org: s.auth.organization }), shallowEqual);
  const { ev, loading } = useSelector(s => ({
    ev:      s.dashboard.eventDetail,
    loading: s.dashboard.actionsLoading,
  }), shallowEqual);

  const [confirmCancel, setConfirmCancel] = useState(false);

  useEffect(() => {
    if (eventId) dispatch(actions.fetchAllData('event', eventId));
  }, [eventId]);

  if (!ev) return <div className="ev-page ev-loading">Loading…</div>;

  const isDraft     = ev.status === 'draft';
  const isPublished = ev.status === 'published';
  const isCancelled = ev.status === 'cancelled';

  const handlePublish = () => {
    dispatch(actions.addData('publish_event', {}, eventId)).then(() => {
      dispatch(actions.fetchAllData('event', eventId));
    });
  };

  const handleCancel = () => {
    dispatch(actions.addData('cancel_event', { reason: 'Cancelled by organizer' }, eventId)).then(() => {
      setConfirmCancel(false);
      dispatch(actions.fetchAllData('event', eventId));
    });
  };

  const handleDelete = () => {
    if (window.confirm('Delete this event? This cannot be undone.')) {
      dispatch(actions.addData('delete_event', {}, eventId)).then(() => {
        navigate('/events/list');
      });
    }
  };

  const kpis = [
    { label: 'Tickets Sold',  value: ev.tickets_sold  ?? '—',  icon: Ticket,      color: '#5341C4', bg: '#F0EEFF' },
    { label: 'Capacity',      value: ev.capacity       ?? '—',  icon: Users,       color: '#2563EB', bg: '#DBEAFE' },
    { label: 'Revenue',       value: ev.revenue ? `₦${Number(ev.revenue).toLocaleString()}` : '—', icon: DollarSign, color: '#16A34A', bg: '#DCFCE7' },
  ];

  return (
    <div className="ev-page">
      {/* Header */}
      <div className="ev-page__header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button className="ev-back" onClick={() => navigate('/events/list')}>
            <ArrowLeft size={16} strokeWidth={1.8} />
          </button>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <h1 className="ev-page__title" style={{ marginBottom: 0 }}>{ev.name}</h1>
              <span className="ev-badge" style={{ background: statusBg[ev.status] || '#F1F5F9', color: statusColor[ev.status] || '#52546A' }}>
                {ev.status || 'draft'}
              </span>
            </div>
            <p className="ev-page__sub" style={{ marginTop: 2 }}>
              {ev.start_date ? new Date(ev.start_date).toLocaleString('en-NG', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}
              {ev.venue ? ` · ${ev.venue}` : ''}
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {isDraft && (
            <>
              <button className="ev-btn ev-btn--outline" onClick={() => navigate(`/events/${eventId}/edit`)}>
                <Edit2 size={14} /> Edit
              </button>
              <button className="ev-btn ev-btn--primary" onClick={handlePublish} disabled={loading}>
                <Send size={14} /> Publish
              </button>
            </>
          )}
          {isPublished && (
            <>
              <button className="ev-btn ev-btn--outline" onClick={() => navigate(`/events/${eventId}/orders`)}>
                <Ticket size={14} /> Orders
              </button>
              <button className="ev-btn ev-btn--danger" onClick={() => setConfirmCancel(true)}>
                <XCircle size={14} /> Cancel Event
              </button>
            </>
          )}
          {!isCancelled && isDraft && (
            <button className="ev-btn ev-btn--ghost" onClick={handleDelete}>
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>

      {/* KPIs */}
      <div className="ev-kpi-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        {kpis.map((k, i) => {
          const Ic = k.icon;
          return (
            <div className="ev-kpi" key={i}>
              <div className="ev-kpi__icon" style={{ background: k.bg, color: k.color }}>
                <Ic size={18} strokeWidth={1.8} />
              </div>
              <div className="ev-kpi__body">
                <div className="ev-kpi__val">{k.value}</div>
                <div className="ev-kpi__label">{k.label}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Details */}
      <div className="ev-section">
        <div className="ev-section__head">
          <span className="ev-section__title">Event Details</span>
        </div>
        <div className="ev-detail-grid">
          {[
            { icon: Calendar, label: 'Start', value: ev.start_date ? new Date(ev.start_date).toLocaleString('en-NG') : '—' },
            { icon: Calendar, label: 'End',   value: ev.end_date   ? new Date(ev.end_date).toLocaleString('en-NG')   : '—' },
            { icon: MapPin,   label: 'Venue', value: ev.venue || '—' },
          ].map(({ icon: Ic, label, value }) => (
            <div className="ev-detail-row" key={label}>
              <span className="ev-detail-row__icon"><Ic size={14} strokeWidth={1.8} color="#9597A6" /></span>
              <span className="ev-detail-row__label">{label}</span>
              <span className="ev-detail-row__value">{value}</span>
            </div>
          ))}
          {ev.description && (
            <div className="ev-detail-row ev-detail-row--full">
              <span className="ev-detail-row__label">Description</span>
              <span className="ev-detail-row__value">{ev.description}</span>
            </div>
          )}
        </div>
      </div>

      {/* Tiers */}
      {ev.tiers?.length > 0 && (
        <div className="ev-section">
          <div className="ev-section__head">
            <span className="ev-section__title">Ticket Tiers</span>
          </div>
          <div className="ev-tiers">
            {ev.tiers.map((tier, i) => (
              <div className="ev-tier" key={i}>
                <div className="ev-tier__name">{tier.name}</div>
                <div className="ev-tier__meta">
                  <span>₦{Number(tier.price).toLocaleString()}</span>
                  <span>{tier.sold ?? 0} / {tier.quantity ?? '∞'} sold</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Cancel confirmation dialog */}
      {confirmCancel && (
        <div className="ev-overlay">
          <div className="ev-dialog">
            <h3>Cancel this event?</h3>
            <p>All ticket holders will be notified. This action cannot be undone.</p>
            <div className="ev-dialog__actions">
              <button className="ev-btn ev-btn--outline" onClick={() => setConfirmCancel(false)}>Keep Event</button>
              <button className="ev-btn ev-btn--danger" onClick={handleCancel} disabled={loading}>Cancel Event</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
